import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, ArrowRight, Loader2 } from 'lucide-react';
import ClientDashboard from './dashboards/ClientDashboard';
import PeerDashboard from './dashboards/PeerDashboard';
import CoordinatorDashboard from './dashboards/CoordinatorDashboard';
import ReferralSourceDashboard from './dashboards/ReferralSourceDashboard';
import AdminDashboard from './dashboards/AdminDashboard';
import ResourcePartnerDashboard from './dashboards/ResourcePartnerDashboard';
import FacilitatorDashboard from './dashboards/FacilitatorDashboard';

const API = process.env.REACT_APP_BACKEND_URL;

function Core8Gate() {
  const navigate = useNavigate();
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4" data-testid="core8-gate">
      <div className="max-w-lg w-full text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Shield className="h-10 w-10 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
          Complete Your CORE-8 Profile
        </h1>
        <p className="text-muted-foreground leading-relaxed">
          Before you can access your dashboard and all program features, we need to learn a little about where you are right now.
          This short 32-question assessment helps your support team understand how to best help you.
        </p>
        <Card className="border-0 shadow-sm rounded-2xl bg-muted/30">
          <CardContent className="pt-5 pb-5 space-y-3 text-left">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">1</span>
              <p>32 honest questions about your life right now — no right or wrong answers</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">2</span>
              <p>Takes about 10-15 minutes to complete</p>
            </div>
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">3</span>
              <p>Your support team will share a personalized strengths summary with you</p>
            </div>
          </CardContent>
        </Card>
        <Button
          data-testid="core8-gate-start-btn"
          onClick={() => navigate('/core8')}
          className="w-full py-3 rounded-xl text-base"
          size="lg"
        >
          Start Your Assessment <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [gateCheck, setGateCheck] = useState(null);
  const [gateLoading, setGateLoading] = useState(false);

  const isClient = user?.role === 'client' || user?.role === 'housing_resident';

  useEffect(() => {
    if (isClient) {
      setGateLoading(true);
      fetch(`${API}/api/core8/gate-check`, {
        credentials: 'include'
      })
        .then(r => r.json())
        .then(data => setGateCheck(data))
        .catch(() => setGateCheck({ required: false, completed: true }))
        .finally(() => setGateLoading(false));
    }
  }, [isClient, token]);

  // Gate loading
  if (isClient && gateLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Gate: client has not completed CORE-8
  if (isClient && gateCheck?.required) {
    return <Core8Gate />;
  }

  const dashboards = {
    client: ClientDashboard,
    peer_specialist: PeerDashboard,
    facilitator: FacilitatorDashboard,
    coordinator: CoordinatorDashboard,
    court_officer: ReferralSourceDashboard,
    referral_source: ReferralSourceDashboard,
    admin: AdminDashboard,
    resource_partner: ResourcePartnerDashboard,
  };
  const Dashboard = dashboards[user?.role] || ClientDashboard;
  return <Dashboard />;
}
